import type { PayexThunkAction } from 'store/configureStore';

import {
  UPLOAD_DOCUMENT_FAILURE,
  UPLOAD_DOCUMENT_REQUEST,
  UPLOAD_DOCUMENT_SUCCESS,
  USE_PAYEX_PAYMENTS_CONTROLLER,
} from 'constants/index';
import type { RootState } from 'reducers/types';
import { getFeature, getPayment, getPaymentId, isFetching } from 'selectors';
import { errorNotifier } from 'utils/errorNotifier/errorNotifier';

const shouldUsePayexService = (state: RootState) =>
  getFeature(state, USE_PAYEX_PAYMENTS_CONTROLLER);

export const generateDocumentUrl =
  (filename: string): PayexThunkAction =>
  async (_dispatch, getState, { apiV3, payex }) => {
    const state = getState();
    const { token } = getPayment(state);
    const id = getPaymentId(state);
    const service = shouldUsePayexService(state) ? payex : apiV3;

    try {
      const response = await service.payments.generateDocumentUrl({
        id,
        token,
        filename,
      });

      if (!response.url) {
        throw new Error('docCollector.error');
      }

      return response.url;
    } catch (error) {
      errorNotifier.notify(error);
      throw error;
    }
  };

export const uploadDocument =
  (
    file: File,
    documentUrl: string,
    // eslint-disable-next-line camelcase
    info: { payex_type?: string } = {},
  ): PayexThunkAction =>
  async (dispatch, getState, { apiV3, payex }) => {
    const state = getState();
    const isFetchingDocuments = isFetching(state, 'documents');
    const service = shouldUsePayexService(state) ? payex : apiV3;

    if (isFetchingDocuments) {
      return;
    }

    dispatch({ type: UPLOAD_DOCUMENT_REQUEST });

    try {
      await service.payments.uploadFileToUrl({
        file,
        url: documentUrl,
        info,
      });

      return dispatch({
        type: UPLOAD_DOCUMENT_SUCCESS,
      });
    } catch (error) {
      const { name } = file;

      errorNotifier.notify(error);

      return dispatch({
        type: UPLOAD_DOCUMENT_FAILURE,
        payload: { name },
      });
    }
  };
